import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, Star, Clock, MapPin, Check, 
  ChevronDown, ChevronUp, User, ArrowLeft, ArrowRight, ShieldCheck 
} from 'lucide-react';
import { toursData } from '../data/tours';
import './TourDetails.css';

export default function TourDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tour = toursData.find((t) => t.id === id);

  const [activeTab, setActiveTab] = useState('overview');
  const [guests, setGuests] = useState(1);
  const [date, setDate] = useState('');
  const [openDay, setOpenDay] = useState(1);

  if (!tour) {
    return (
      <div className="tour-not-found container section">
        <div className="glass-card not-found-card">
          <h2>Tour Package Not Found</h2>
          <p>We couldn't find the package you are looking for. It might have been updated or removed.</p>
          <Link to="/" className="btn btn-primary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { 
    title, category, price, originalPrice, duration, rating, 
    reviewsCount, image, tag, description, highlights, itinerary 
  } = tour;

  const handleGuestsChange = (amount) => {
    setGuests(prev => Math.max(1, prev + amount));
  };

  const handleBookNow = (e) => {
    e.preventDefault();
    if (!date) {
      alert("Please select a departure date.");
      return;
    }
    navigate(`/book?tour=${id}&guests=${guests}&date=${date}`);
  };

  const toggleDay = (dayNum) => {
    setOpenDay(openDay === dayNum ? null : dayNum);
  };

  return (
    <div className="tour-details-page">
      {/* Hero Banner Section */}
      <section className="tour-details-hero" style={{ backgroundImage: `url(${image})` }}>
        <div className="hero-overlay"></div>
        <div className="hero-nav container">
          <Link to={category === 'Domestic' ? '/domestic' : '/international'} className="back-link">
            <ArrowLeft size={16} /> Back to {category} Tours
          </Link>
        </div>
        <div className="hero-content container">
          <div className="hero-badges">
            <span className={`badge ${category.toLowerCase() === 'domestic' ? 'badge-domestic' : 'badge-international'}`}>
              {category} Tour
            </span>
            {tag && <span className="badge badge-gold">{tag}</span>}
          </div>
          <h1 className="tour-title">{title}</h1>
          <div className="tour-meta">
            <div className="meta-item">
              <Star className="star-icon" size={16} />
              <span><strong>{rating}</strong> ({reviewsCount} reviews)</span>
            </div>
            <div className="meta-item">
              <Clock size={16} />
              <span>{duration}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Details Grid */}
      <section className="tour-main-content container section">
        <div className="details-grid">
          {/* Left Column (Information) */}
          <div className="details-info-area">
            {/* Tabs Selector */}
            <div className="tabs-header">
              <button 
                className={`tab-btn ${activeTab === 'overview' ? 'tab-btn-active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button 
                className={`tab-btn ${activeTab === 'highlights' ? 'tab-btn-active' : ''}`}
                onClick={() => setActiveTab('highlights')}
              >
                Highlights
              </button>
              <button 
                className={`tab-btn ${activeTab === 'itinerary' ? 'tab-btn-active' : ''}`}
                onClick={() => setActiveTab('itinerary')}
              >
                Itinerary
              </button>
            </div>

            {/* Tabs Content */}
            <div className="tab-content-panel glass-card">
              {activeTab === 'overview' && (
                <div className="tab-pane overview-pane">
                  <h3>About the Package</h3>
                  <p>{description}</p>
                  
                  <div className="highlights-preview" style={{ marginTop: '24px' }}>
                    <h4>Package Highlights Included:</h4>
                    <ul className="details-highlights-list">
                      {highlights.slice(0, 3).map((hl, index) => (
                        <li key={index}>
                          <Check size={18} className="check-icon" />
                          <span>{hl}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'highlights' && (
                <div className="tab-pane highlights-pane">
                  <h3>Highlights of the Journey</h3>
                  <ul className="details-highlights-list full-list">
                    {highlights.map((hl, index) => (
                      <li key={index}>
                        <Check size={18} className="check-icon" />
                        <span>{hl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeTab === 'itinerary' && (
                <div className="tab-pane itinerary-pane">
                  <h3>Day-by-Day Travel Itinerary</h3>
                  <div className="accordion-list">
                    {itinerary.map((dayPlan) => (
                      <div key={dayPlan.day} className={`accordion-item ${openDay === dayPlan.day ? 'accordion-open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleDay(dayPlan.day)}>
                          <span className="day-number">Day {dayPlan.day}</span>
                          <span className="day-title">{dayPlan.title}</span>
                          {openDay === dayPlan.day ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        {openDay === dayPlan.day && (
                          <div className="accordion-content">
                            <p>{dayPlan.description}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Travel Guarantees card */}
            <div className="guarantees-card glass-card">
              <ShieldCheck size={32} className="guarantees-icon" />
              <div>
                <h4>Holiday Kart Safety & Flex Guarantee</h4>
                <p>Enjoy free date modifications up to 15 days before departure. Medical insurance cover and experienced English-speaking guides are standard on all our programs.</p>
              </div>
            </div>
          </div>

          {/* Right Column (Sticky Booking Sidebar) */}
          <div className="details-sidebar-area">
            <div className="booking-sidebar-card glass-card">
              <div className="pricing-box">
                <span className="sidebar-price-label">Price per person</span>
                <div className="sidebar-price-row">
                  <span className="sidebar-price-current">₹{price.toLocaleString('en-IN')}</span>
                  {originalPrice && <span className="sidebar-price-original">₹{originalPrice.toLocaleString('en-IN')}</span>}
                </div>
              </div>

              <form className="sidebar-booking-form" onSubmit={handleBookNow}>
                <div className="form-group">
                  <label className="form-label">Departure Date</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={date} 
                    onChange={(e) => setDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required 
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Guests Count</label>
                  <div className="guests-counter">
                    <button 
                      type="button" 
                      className="counter-btn"
                      onClick={() => handleGuestsChange(-1)}
                      disabled={guests <= 1}
                    >
                      -
                    </button>
                    <span className="guests-value">{guests}</span>
                    <button 
                      type="button" 
                      className="counter-btn"
                      onClick={() => handleGuestsChange(1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Subtotal calculation */}
                <div className="booking-summary">
                  <div className="summary-row">
                    <span>Base Price</span>
                    <span>₹{price.toLocaleString('en-IN')} × {guests}</span>
                  </div>
                  <div className="summary-row subtotal-row">
                    <span>Subtotal</span>
                    <span>₹{(price * guests).toLocaleString('en-IN')}</span>
                  </div>
                  <p className="gst-label">*Taxes and local fees calculated at checkout</p>
                </div>

                <button type="submit" className="btn btn-primary booking-submit-btn">
                  <span>Book This Trip</span>
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
